import { gapiCall } from './gapi';

// invitations action types

// request invitations by user ID
export const REQUEST_INVITATIONS = "REQUEST_INVITATIONS";
export const REQUEST_INVITATIONS_SUCCESS = "REQUEST_INVITATIONS_SUCCESS";
export const REQUEST_INVITATIONS_ERROR = "REQUEST_INVITATIONS_ERROR";

// accept invitation
export const ACCEPT_INVITATION = "ACCEPT_INVITATION";
export const ACCEPT_INVITATION_SUCCESS = "ACCEPT_INVITATION_SUCCESS";
export const ACCEPT_INVITATION_ERROR = "ACCEPT_INVITATION_ERROR";

// delete invitation (cancel invitation or deny invitation)
export const DELETE_INVITATION = "DELETE_INVITATION";
export const DELETE_INVITATION_SUCCESS = "DELETE_INVITATION_SUCCESS";
export const DELETE_INVITATION_ERROR = "DELETE_INVITATION_ERROR";

// request invitation by ID
export const REQUEST_INVITATION = "REQUEST_INVITATION";
export const REQUEST_INVITATION_SUCCESS = "REQUEST_INVITATION_SUCCESS";
export const REQUEST_INVITATION_ERROR = "REQUEST_INVITATION_ERROR";

// store url signin redirection (in order to redirect to the targeted url after signin)
export const STORE_LOCAL_REDIRECT_SIGNIN_URL = "STORE_LOCAL_REDIRECT_SIGNIN_URL";
export const REMOVE_LOCAL_REDIRECT_SIGNIN_URL = "REMOVE_LOCAL_REDIRECT_SIGNIN_URL";

/**
 * Request invitations by user id action creator
 * @param {String} userId 
 */
const requestInvitations = userId => ({
  type: REQUEST_INVITATIONS,
  userId
})

/**
 * Request invitations by user id success action creator
 * @param {String} userId 
 * @param {Array} items Invitations for this user
 */
const requestInvitationsSuccess = (userId, items) => ({
  type: REQUEST_INVITATIONS_SUCCESS,
  userId,
  items
})

/**
 * Request invitations by user id error action creator
 * @param {String} userId 
 * @param {Error} error 
 */
const requestInvitationsError = (userId, error) => ({
  type: REQUEST_INVITATIONS_ERROR,
  userId,
  error
})

/**
 * Accept invitation action creator
 * @param {String} invitationId The invitation to accept
 * @param {String} userId The user ID to who the licence is granted
 */
const acceptInvitationRequest = (invitationId, userId) => ({
  type: ACCEPT_INVITATION,
  invitationId,
  userId
})

/**
 * Accept invitation success action creator
 * When the invitation has been accepted, it is deleted and a licence is created
 * @param {String} invitationId The accepted invitation
 * @param {String} userId The user ID to who the licence is granted
 * @param {Object} licence The licence created by accepting the invitation
 */
const acceptInvitationSuccess = (invitationId, userId, licence) => ({
  type: ACCEPT_INVITATION_SUCCESS,
  invitationId,
  userId,
  licence
})

/**
 * Accept invitation error action creator
 * @param {String} invitationId The accepted invitation
 * @param {String} userId The user ID to who the licence is granted
 * @param {Error} error 
 */
const acceptInvitationError = (invitationId, userId, error) => ({
  type: ACCEPT_INVITATION_ERROR,
  invitationId,
  userId,
  error
})

/**
 * Delete invitation action creator
 * Called when cancelling an invite or denying an invite
 * @param {String} invitationId 
 */
const deleteInvitationRequest = (invitationId) => ({
  type: DELETE_INVITATION,
  invitationId
})

/**
 * Delete invitation success action creator
 * @param {String} invitationId 
 */
const deleteInvitationSuccess = (invitationId) => ({
  type: DELETE_INVITATION_SUCCESS,
  invitationId
})

/**
 * Delete invitation error action creator
 * @param {String} invitationId 
 * @param {Error} error 
 */
const deleteInvitationError = (invitationId, error) => ({
  type: DELETE_INVITATION_ERROR,
  invitationId,
  error
})

/**
 * Request invitation action creator
 * @param {String} invitationId 
 */
const requestInvitation = (invitationId) => ({
  type: REQUEST_INVITATION,
  invitationId
})

/**
 * Request invitation success action creator
 * @param {Object} invitation The fetched invitation
 */
const requestInvitationSuccess = (invitation) => ({
  type: REQUEST_INVITATION_SUCCESS,
  invitation
})

/**
 * Request invitation error action creator
 * @param {String} invitationId The requested invitation ID
 * @param {Error} error 
 */
const requestInvitationError = (invitationId, error) => ({
  type: REQUEST_INVITATION_ERROR,
  invitationId,
  error
})

export const allActions = {
  requestInvitation,
  requestInvitationError,
  requestInvitationSuccess,
  requestInvitations,
  requestInvitationsSuccess,
  requestInvitationsError,
  acceptInvitationRequest,
  acceptInvitationError,
  acceptInvitationSuccess,
  deleteInvitationRequest,
  deleteInvitationSuccess,
  deleteInvitationError
}

/**
 * Fetches the invitations for a user,
 * ie the invitations sent to the email of the user,
 * @param {String} userId 
 */
export const fetchInvitationsByUserId = userId => dispatch =>
  dispatch(requestInvitations(userId))
  && dispatch(gapiCall(client => client.billing.users.invitations.get({ userId })))
    .then(res => dispatch(requestInvitationsSuccess(userId, res.items)))
    .catch(error => dispatch(requestInvitationsError(userId, error)))

/**
 * Accepts an invitation for a user
 * @param {String} invitationId 
 * @param {String} userId 
 */
export const acceptInvitation = (invitationId, token, userId) => dispatch =>
  dispatch(acceptInvitationRequest(invitationId, userId))
  && dispatch(gapiCall(client => client.billing.invitations.accept({ invitationId, token, userId })))
    .then(licence => dispatch(acceptInvitationSuccess(invitationId, userId, licence)))
    .catch(error => dispatch(acceptInvitationError(invitationId, userId, error)))

/**
 * Deletes an invitation (deny or cancel invitation)
 * @param {String} invitationId 
 */
export const deleteInvitation = (invitationId, token) => dispatch =>
  dispatch(deleteInvitationRequest(invitationId))
  && dispatch(gapiCall(client => client.billing.invitations.delete({ invitationId, token })))
    .then(res => dispatch(deleteInvitationSuccess(invitationId)))
    .catch(error => dispatch(deleteInvitationError(invitationId, error)))

/**
 * Fetches an invitation by ID
 * @param {String} invitationId 
 */
export const fetchInvitation = (invitationId, token) => dispatch =>
  dispatch(requestInvitation(invitationId))
  && dispatch(gapiCall(client => client.billing.invitations.get({ invitationId, token })))
    .then(invitation => dispatch(requestInvitationSuccess(invitation)))
    .catch(error => dispatch(requestInvitationError(invitationId, error)))

const storeLocalRedirectSigninUrl = (url) => ({
  type: STORE_LOCAL_REDIRECT_SIGNIN_URL,
  url,
});

const removeLocalRedirectSigninUrl = (url) => ({
  type: REMOVE_LOCAL_REDIRECT_SIGNIN_URL,
  url,
});

export const REDIRECT_SIGNIN_URL = "REDIRECT_SIGNIN_URL";

export const doStoreRedirectSigninUrl = url => dispatch => {
  localStorage.setItem(REDIRECT_SIGNIN_URL, url);
  dispatch( storeLocalRedirectSigninUrl(url) );
}

export const doRemoveLocalRedirectSigninUrl = url => dispatch => {
  localStorage.removeItem(REDIRECT_SIGNIN_URL);
  dispatch( removeLocalRedirectSigninUrl(url) );
}
    


// WEBPACK FOOTER //
// ./src/actions/invitations.js